import {
  useEffect,
  useState
} from "react";

import api from "../api";

import {
  toDate,
  toInputDate
} from "../utils/dateUtils";

function ReservationForm({
  appartement,
  sejourId,
  initialData,
  mode,
  onClose,
  dateDebut: initialDebut,
  dateFin: initialFin
}) {

  const [clients, setClients] =
    useState([]);

  const [sejours, setSejours] =
    useState([]);

  const [clientId, setClientId] =
    useState("");

  const [typeOccupation, setTypeOccupation] =
    useState("individuel");

  const [nombreOccupants, setNombreOccupants] =
    useState(1);

  const [dateDebut, setDateDebut] =
    useState(
      toInputDate(
        initialDebut
      )
    );

  const [dateFin, setDateFin] =
    useState(
      toInputDate(
        initialFin
      )
    );

  const [prixNuitee, setPrixNuitee] =
    useState("");

  const [errors, setErrors] =
    useState({});

  const [info, setInfo] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  // =====================
  // TODAY
  // =====================

  const getToday = () =>
    toInputDate(
      new Date()
    );

  // =====================
  // LOAD
  // =====================

  useEffect(() => {

    const load =
      async () => {

        try {

          const resClients =
            await api.get(
              "/clients"
            );

          setClients(
            resClients.data ||
              []
          );

          const resSejours =
            await api.get(
              "/sejours"
            );

          setSejours(
            resSejours.data ||
              []
          );

        } catch (err) {

          console.error(err);

          setInfo(
            "Erreur chargement données"
          );

        }
      };

    load();

  }, []);

  // =====================
  // APPARTEMENT
  // =====================

  useEffect(() => {

    if (appartement) {

      setPrixNuitee(
        appartement.prix_nuitee ||
          ""
      );

    }

  }, [appartement]);

  // =====================
  // EDIT MODE
  // =====================

  useEffect(() => {

    if (
      mode === "edit" &&
      initialData
    ) {

      setDateDebut(
        toInputDate(
          initialData.date_debut
        )
      );

      setDateFin(
        toInputDate(
          initialData.date_fin_prevue
        )
      );

      setClientId(
        initialData.client_id ||
          ""
      );

    } else {

      if (!initialDebut) {

        setDateDebut(
          getToday()
        );

      }
    }

  }, [
    initialData,
    mode,
    initialDebut
  ]);

  useEffect(() => {

    if (initialDebut) {

      setDateDebut(
        toInputDate(
          initialDebut
        )
      );

    }

    if (initialFin) {

      setDateFin(
        toInputDate(
          initialFin
        )
      );

    }

  }, [
    initialDebut,
    initialFin
  ]);

  // =====================
  // OVERLAP
  // =====================

  const isOverlap =
    (
      s,
      start2,
      end2
    ) => {

      const start1 =
        toDate(
          s.date_debut
        );

      const end1 =
        toDate(
          s.date_fin_prevue ||
            s.date_debut
        );

      const s2 =
        toDate(start2);

      const e2 =
        toDate(end2);

      if (
        !start1 ||
        !end1 ||
        !s2 ||
        !e2
      ) {
        return false;
      }

      return (
        start1 < e2 &&
        s2 < end1
      );
    };

  const getAppartementConflict =
    () => {

      for (let s of sejours) {

        // ==========================================
        // 🚫 STATUTS QUI NE BLOQUENT PAS
        // ==========================================

        if (
          [
            "annule",
            "expire",
            "no_show",
            "termine"
          ].includes(s.statut)
        ) {
          continue;
        }

        // ==========================================
        // 🔄 MODIFICATION : ignorer le séjour actuel
        // ==========================================

        if (
          mode === "edit" &&
          (
            s.id === sejourId ||
            s.sejour_id === sejourId
          )
        ) {
          continue;
        }

        // ==========================================
        // 🏠 AUTRE APPARTEMENT
        // ==========================================

        if (
          s.appartement_id != appartement.id
        ) {
          continue;
        }

        // ==========================================
        // 📅 CHEVAUCHEMENT
        // ==========================================

        if (
          isOverlap(
            s,
            dateDebut,
            dateFin || dateDebut
          )
        ) {
          return s;
        }
      }

      return null;
    };
    
  const getClientConflict =
    () => {

      for (let s of sejours) {

        if (
          mode ===
            "edit" &&
          (s.id ===
            sejourId ||
            s.sejour_id ===
              sejourId)
        ) {
          continue;
        }

        if (
          s.client_id !=
          clientId
        ) {
          continue;
        }

        if (
          isOverlap(
            s,
            dateDebut,
            dateFin ||
              dateDebut
          )
        ) {

          return s;

        }
      }

      return null;
    };

  // =====================
  // VALIDATE
  // =====================

  const validate =
    () => {

      let errs = {};

      if (!clientId) {

        errs.client =
          "Client requis";

      }

      if (!dateDebut) {

        errs.dateDebut =
          "Date début requise";

      }

      if (!dateFin) {

        errs.dateFin =
          "Date fin requise";

      }

      if (
        dateFin &&
        toDate(
          dateFin
        ) <=
          toDate(
            dateDebut
          )
      ) {

        errs.dateFin =
          "Minimum 1 nuit";

      }

      if (
        toDate(
          dateDebut
        ) <
        toDate(
          getToday()
        )
      ) {

        errs.dateDebut =
          "Date passée interdite";

      }

      if (!prixNuitee) {

        errs.prix =
          "Prix requis";

      }

      const clientConflict =
        getClientConflict();

      if (
        clientConflict
      ) {

        errs.client = `Client déjà réservé du ${clientConflict.date_debut} au ${clientConflict.date_fin_prevue}`;

      }

      const appartConflict =
        getAppartementConflict();

      if (
        appartConflict
      ) {

        errs.dateDebut = `Appartement occupé du ${appartConflict.date_debut} au ${appartConflict.date_fin_prevue}`;

      }

      setErrors(errs);

      return (
        Object.keys(errs)
          .length === 0
      );
    };

  // =====================
  // SUBMIT
  // =====================

  const handleSubmit =
    async () => {

      if (
        !validate()
      ) {
        return;
      }

      const resume = `
Confirmer la réservation :

📅 Du ${dateDebut} au ${dateFin}
👤 Client ID : ${clientId}
💰 Prix : ${prixNuitee}$ / nuit

Continuer ?
`;

      if (
        !window.confirm(
          resume
        )
      ) {
        return;
      }

      if (
        !window.confirm(
          "⚠️ Validation finale. Confirmer définitivement ?"
        )
      ) {
        return;
      }

      setLoading(true);

      setInfo("");

      try {

        const url =
          mode ===
          "edit"
            ? `/sejours/${sejourId}`
            : "/sejours";

        const method =
          mode ===
          "edit"
            ? "put"
            : "post";

        const body = {
          client_id:
            clientId,

          appartement_id:
            appartement.id,

          date_debut:
            dateDebut,

          date_fin_prevue:
            dateFin,

          prix_nuitee_applique:
            prixNuitee,

          type_occupation: typeOccupation,

          nombre_occupants: nombreOccupants
        };

        await api({
          url,
          method,
          data: body
        });

        onClose();

        window.location.reload();

      } catch (err) {

        console.error(err);

        setInfo(
          err?.response
            ?.data?.error ||
            "Erreur serveur"
        );

      } finally {

        setLoading(false);

      }
    };

  const handleOccupationChange = (e) => {

      const type = e.target.value;

      setTypeOccupation(type);

      switch (type) {

          case "individuel":
              setNombreOccupants(1);
              break;

          case "couple":
              setNombreOccupants(2);
              break;

          case "famille":
              setNombreOccupants(3);
              break;

          case "groupe":
              setNombreOccupants(5);
              break;

          default:
              setNombreOccupants(1);

      }

  };

  // =====================
  // RENDER
  // =====================

  return (
    <div className="w-full">

      <div className="bg-white rounded-2xl shadow-2xl p-5 md:p-6 w-full max-w-md">

        {/* HEADER */}
        <h3 className="text-2xl font-bold mb-5 text-center">

          {mode === "edit"
            ? "Modifier réservation"
            : "Nouvelle réservation"}

        </h3>

        {/* INFO */}
        {info && (

          <div className="bg-red-100 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-sm">
            {info}
          </div>

        )}

        <div className="space-y-4">

          {/* CLIENT */}
          <div>

            <label className="block text-sm text-gray-600 mb-2">
              Client
            </label>

            <select
              value={
                clientId
              }
              onChange={(e) =>
                setClientId(
                  e.target.value
                )
              }
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
            >

              <option value="">
                -- Choisir --
              </option>

              {clients.map(
                (c) => (

                  <option
                    key={c.id}
                    value={c.id}
                  >
                    {c.prenom}{" "}
                    {c.nom}
                  </option>

                )
              )}

            </select>

            {errors.client && (

              <p className="text-red-500 text-xs mt-1">
                {
                  errors.client
                }
              </p>

            )}

          </div>

          {/* DATE DEBUT */}
          <div>

            <label className="block text-sm text-gray-600 mb-2">
              Date début
            </label>

            <input
              type="date"
              value={
                dateDebut
              }
              min={getToday()}
              onChange={(e) =>
                setDateDebut(
                  e.target.value
                )
              }
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
            />

            {errors.dateDebut && (

              <p className="text-red-500 text-xs mt-1">
                {
                  errors.dateDebut
                }
              </p>

            )}

          </div>

          {/* DATE FIN */}
          <div>

            <label className="block text-sm text-gray-600 mb-2">
              Date fin
            </label>

            <input
              type="date"
              value={dateFin}
              min={dateDebut}
              onChange={(e) =>
                setDateFin(
                  e.target.value
                )
              }
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
            />

            {errors.dateFin && (

              <p className="text-red-500 text-xs mt-1">
                {
                  errors.dateFin
                }
              </p>

            )}

          </div>

          {/* PRIX */}
          <div>

            <label className="block text-sm text-gray-600 mb-2">
              Prix nuitée ($)
            </label>

            <input
              type="number"
              value={
                prixNuitee
              }
              onChange={(e) =>
                setPrixNuitee(
                  e.target.value
                )
              }
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
            />

            {errors.prix && (

              <p className="text-red-500 text-xs mt-1">
                {
                  errors.prix
                }
              </p>

            )}

          </div>

          {/* ===================== */}
          {/* OCCUPATION */}
          {/* ===================== */}

          <div className="border rounded-xl p-4 bg-gray-50">

              <h4 className="font-semibold text-gray-700 mb-3">
                  👥 Occupation
              </h4>

              <label className="block text-sm text-gray-600 mb-2">
                  Type d'occupation
              </label>

              <select
                  value={typeOccupation}
                  onChange={handleOccupationChange}
                  className="w-full border rounded-xl px-4 py-3 mb-3"
              >
                  <option value="individuel">
                      Individuel
                  </option>

                  <option value="couple">
                      Couple
                  </option>

                  <option value="famille">
                      Famille
                  </option>

                  <option value="groupe">
                      Groupe
                  </option>
              </select>

              <label className="block text-sm text-gray-600 mb-2">
                  Nombre d'occupants
              </label>

              <input
                  type="number"
                  min="1"
                  value={nombreOccupants}
                  onChange={(e)=>
                      setNombreOccupants(
                          Number(e.target.value)
                      )
                  }
                  className="w-full border rounded-xl px-4 py-3"
              />

          </div>

          {/* ACTIONS */}
          <div className="flex flex-col md:flex-row gap-3 pt-2">

            <button
              onClick={
                handleSubmit
              }
              disabled={
                loading
              }
              className="flex-1 bg-blue-600 hover:bg-blue-700 transition text-white py-3 rounded-xl disabled:opacity-50"
            >
              {loading
                ? "Envoi..."
                : "Valider"}
            </button>

            <button
              onClick={
                onClose
              }
              className="flex-1 bg-gray-300 hover:bg-gray-400 transition py-3 rounded-xl"
            >
              Annuler
            </button>

          </div>

        </div>

      </div>

    </div>
  );
}

export default ReservationForm;